import type { QrLegacyConfig, QrStyleOptions } from '@/typings/customilyElements/canvasElements';

const baseUrl = process.env.VUE_APP_CUSTOMILY_STOREFRONT_SERVICE_API_URL;

const decodeBase64Svg = (base64: string): string => {
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

const generateQrCode = async (body: Record<string, unknown>): Promise<string> => {
    const response = await fetch(`${baseUrl}/qr-code-generator/generate`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json'
        }
    });

    if (!response.ok) {
        let messageValue = '';

        try {
            const error = await response.json();
            messageValue = error.message;
        } catch {
            // Backend returned non-JSON (HTML/plain text)
            messageValue = await response.text();
        }

        throw new Error(`Failed to generate QR code: ${messageValue || response.statusText}`);
    }

    const { svg } = (await response.json()) as { svg?: string };
    if (!svg) throw new Error('QR code generator returned no svg');

    return decodeBase64Svg(svg);
};

/**
 * Generates a styled QR code and returns its SVG markup, ready to be set as a vector
 *  placeholder's `svgString`.
 */
export const generateStyledQrCode = (options: QrStyleOptions): Promise<string> => generateQrCode({ options });

/**
 * Same generator, fed with the legacy config of QR elements created before the styled options.
 *  The service switches to its legacy mode when the body carries `text` instead of `options`.
 */
export const generateLegacyQrCode = (config: QrLegacyConfig): Promise<string> =>
    generateQrCode({
        text: config.text,
        darkColor: config.darkColor,
        lightColor: config.lightColor,
        pixelSize: config.pixelSize
    });
